/**
 * Time estimates for the deposit/withdraw progress bars.
 *
 * These sagas have no fine-grained progress to report — they are waiting on blocks — so the bar is
 * an estimate driven by which chain the current step runs on:
 *
 *   HyperEVM  ~3s   ~1s blocks from our service wallets, plus a little inclusion slack. Budget
 *                   per tx.
 *   Cronos    ~15s  ordinary ~6s blocks, a couple of confirmations. Budget per operation.
 *   Bridge    ~10s  the cross-chain fill itself once both legs are funded.
 *
 * Deliberately keyed on the backend's `step`, not its human-readable `message`: the message is
 * copy and changes freely, the step is the state machine.
 *
 * A step that is waiting on the USER (a wallet signature) has no estimate at all — running a
 * countdown against someone deciding whether to click Confirm invents a deadline that does not
 * exist and makes a normal pause look like a stall.
 */

/** Per-transaction budget on HyperEVM: a ~1s block plus inclusion slack. */
export const HYPEREVM_TX_ESTIMATE_MS = 3_000;
/** Per-operation budget on Cronos (~6s blocks). */
export const CRONOS_TX_ESTIMATE_MS = 15_000;
/**
 * Per-operation budget on Robinhood Chain (Arbitrum Orbit, sub-second blocks). The chain itself
 * is near instant; the budget is the bridge's confirmation wait on top of it.
 */
export const ROBINHOOD_TX_ESTIMATE_MS = 8_000;
/**
 * HyperCore crediting a funded adapter: the balance shows up on the next state push (~15s cadence),
 * not on the next block.
 */
export const HYPERCORE_CREDIT_ESTIMATE_MS = 15_000;
/**
 * The HyperCore funding stage is TWO service-wallet transactions in a row (approve, then fund),
 * then the credit. Seen from its first step it is one continuous wait of that
 * length; seen from its second (a page that attached late) only the fund and the credit remain.
 */
export const HYPERCORE_FUNDING_ESTIMATE_MS =
    2 * HYPEREVM_TX_ESTIMATE_MS + HYPERCORE_CREDIT_ESTIMATE_MS;
export const HYPERCORE_FUNDING_REMAINDER_ESTIMATE_MS =
    HYPEREVM_TX_ESTIMATE_MS + HYPERCORE_CREDIT_ESTIMATE_MS;

/**
 * The user's own chain, per origin. Keyed by chain id because the two anchors are nothing alike:
 * Cronos blocks are ~6s, Robinhood's are a fraction of a second. Anything unknown is budgeted like
 * Cronos, the longer of the two, so a bar never finishes ahead of the chain.
 */
const ORIGIN_TX_ESTIMATES: Record<number, number> = {
    25: CRONOS_TX_ESTIMATE_MS,
    4663: ROBINHOOD_TX_ESTIMATE_MS,
};

export function originTxEstimateMs(chainId: number | null | undefined): number {
    return (chainId !== null && chainId !== undefined && ORIGIN_TX_ESTIMATES[chainId]) || CRONOS_TX_ESTIMATE_MS;
}
/**
 * The cross-chain fill, in either direction.
 *
 * Dominated by the bridge's own fill, not by block time: an inbound fill still has to be relayed
 * and included on HyperEVM, and an outbound one is initiated from the exit wallet and settled on
 * the destination chain.
 */
export const BRIDGE_ESTIMATE_MS = 20_000;

const STEP_ESTIMATES: Record<string, number> = {
    // ── withdraw: custodial on HyperEVM -> user's chain ────────────────────────────────
    // every one of these is a service-wallet transaction on HyperEVM
    'withdraw.preparing': HYPEREVM_TX_ESTIMATE_MS,
    'withdraw.hypercoreSweep.waiting': HYPEREVM_TX_ESTIMATE_MS,
    'withdraw.adapterFlush.waiting': HYPEREVM_TX_ESTIMATE_MS,
    'withdraw.exitSweep.waiting': HYPEREVM_TX_ESTIMATE_MS,
    // the hop -> destination fill
    'withdraw.relay.filling': BRIDGE_ESTIMATE_MS,

    // ── deposit: user's chain -> custodial on HyperEVM (the same path in reverse) ───────
    // the origin-chain transaction; resolved per chain in phaseEstimateMs, this is the default
    submittingRelay: CRONOS_TX_ESTIMATE_MS,
    // provisioning the custodial wallet is a HyperEVM transaction from a service wallet
    creatingWallet: HYPEREVM_TX_ESTIMATE_MS,
    // the HyperCore funding stage: approve, fund, credit. `submitting`
    // is the stage seen from its start, `waiting` what is left once the fund tx is out. They
    // share ONE timer (see phaseTimerKey), so the bar does not restart halfway through.
    'deposit.hypercoreFunding.submitting': HYPERCORE_FUNDING_ESTIMATE_MS,
    'deposit.hypercoreFunding.waiting': HYPERCORE_FUNDING_REMAINDER_ESTIMATE_MS,
    'deposit.hypercoreFunding.deferred': HYPEREVM_TX_ESTIMATE_MS,

    // the deposit's inbound fill
    'deposit.relay.bridging': BRIDGE_ESTIMATE_MS,

    // the withdrawal's outbound fill
    'withdraw.relay.bridging': BRIDGE_ESTIMATE_MS,

    // emitted by both directions; they cost the same, so no direction is needed to resolve it
    bridging: BRIDGE_ESTIMATE_MS,

    // ── migrate: custodial on HyperEVM -> the user's Snappy Wallet on HyperEVM ──────────
    // the withdrawal's first legs, then a plain payout and an optional HYPE top-up, all
    // service-wallet transactions on HyperEVM; the sweep also waits for HyperCore to credit
    // the adapter before the flush can move it.
    'migrate.preparing': HYPEREVM_TX_ESTIMATE_MS,
    'migrate.hypercoreSweep.waiting':
        HYPEREVM_TX_ESTIMATE_MS + HYPERCORE_CREDIT_ESTIMATE_MS,
    'migrate.adapterFlush.waiting': HYPEREVM_TX_ESTIMATE_MS,
    'migrate.transfer.waiting': HYPEREVM_TX_ESTIMATE_MS,
    'migrate.gas.waiting': HYPEREVM_TX_ESTIMATE_MS,
};

/** Steps that are blocked on a human, where a timer would be dishonest. */
const USER_BLOCKED_STEPS = new Set([
    'awaitingWalletSignature',
    'authorizationRequired',
]);

/** Steps where nothing is being worked on, so there is nothing to estimate. */
const IDLE_STEPS = new Set([
    'queued',
    'settled',
    'failed',
    'refunded',
    'retrying',
    'walletReady',
    // deliberately unmapped: `preparing` belongs to AGENT SETUP, not to a deposit, and agent setup
    // is a ~75s HyperEVM operation with its own bar (ReactivationProgress). Estimating it here as a
    // short Cronos step would show a bar finishing in 15s against a minute-long wait.
    'preparing',
    // Pre-signature bookkeeping: validating input, quoting the bridge, writing the transfer row.
    // These are quick API/database calls, NOT block waits, and they run BEFORE the wallet prompt —
    // giving them a 15s block-time bar is what made a countdown appear and then get interrupted by
    // MetaMask. Nothing is on-chain yet, so nothing is estimated.
    'validating',
    'checkingTradingWallet',
    'checkingOriginWallet',
    'quotingRelay',
    'recordingTransfer',
    // agent setup renders its own bar (ReactivationProgress) from the job's createdAt, so these
    // must not also drive the deposit/withdraw bar.
    'agent.adapter',
    'agent.approval',
    'agent.bootstrap',
    'agent.unified',
    'bootstrapApproval',
    'bootstrapFund',
]);

/**
 * Estimated duration of the current step, or null when a bar should not be shown at all
 * (unknown step, waiting on the user, or nothing in flight).
 */
export function phaseEstimateMs(
    step: string | null | undefined,
    options: { chainId?: number | null } = {},
): number | null {
    if (!step) return null;
    if (USER_BLOCKED_STEPS.has(step) || IDLE_STEPS.has(step)) return null;
    if (step === 'submittingRelay') return originTxEstimateMs(options.chainId);
    return STEP_ESTIMATES[step] ?? null;
}

/**
 * Steps that are one continuous wait share a timer key, so the bar keeps running across them
 * instead of restarting: the HyperCore funding's approve and fund legs are one stage to the user
 * ("moving funds to your trading account"), and a bar that reset to zero between them read as the
 * same step looping.
 */
const TIMER_GROUPS: Record<string, string> = {
    'deposit.hypercoreFunding.submitting': 'deposit.hypercoreFunding',
    'deposit.hypercoreFunding.waiting': 'deposit.hypercoreFunding',
};

export function phaseTimerKey(step: string | null | undefined): string {
    if (!step) return '';
    return TIMER_GROUPS[step] ?? step;
}

/**
 * The "move to trading balance" route (`custodialBalance`) never bridges: its funds are already in
 * the custodial wallet on HyperEVM, and the generic `bridging` step it passes through on the way to
 * the funding stage is the same HyperCore funding wait. Reading it as a 75s "Bridging funds" bar
 * showed a bridge that did not exist and then restarted the bar when the real stage arrived.
 */
export function normalizeDepositStep(
    step: string | null | undefined,
    route: string | null | undefined,
): string | null | undefined {
    if (route === 'custodialBalance' && step === 'bridging') {
        return 'deposit.hypercoreFunding.submitting';
    }
    return step;
}

/**
 * NOTE: there is deliberately no status-based fallback.
 *
 * The client's own `submitting` status is set BEFORE the wallet signature prompt appears, so
 * estimating from it started a 15s countdown while the user was still looking at MetaMask — the
 * bar was most of the way through before they had even signed. A backend `step` only exists once
 * the job has been accepted and is doing work, which is exactly the "the tx is actually in flight"
 * evidence the bar needs. Until one arrives, no bar is shown.
 */

/**
 * Progress of the current phase against its estimate.
 *
 * Caps just short of full: these phases end when the chain says so, and a bar sitting at 100%
 * while the spinner keeps turning reads as a hang. Past the estimate it stops pretending to
 * predict and says it is still going.
 */
export function phaseProgress(
    startedAtMs: number | null,
    nowMs: number,
    estimateMs: number | null,
): { percent: number; secondsElapsed: number; overrun: boolean } {
    if (startedAtMs === null || !Number.isFinite(startedAtMs) || !estimateMs) {
        return { percent: 0, secondsElapsed: 0, overrun: false };
    }
    // a clock skew must never render a negative bar
    const elapsed = Math.max(0, nowMs - startedAtMs);
    return {
        percent: Math.min(95, (elapsed / estimateMs) * 100),
        secondsElapsed: Math.floor(elapsed / 1000),
        overrun: elapsed > estimateMs,
    };
}

/**
 * What each step is actually doing, and where it sits in the sequence.
 *
 * The backend labels FOUR different withdrawal steps "Preparing withdrawal": pulling funds out of
 * the custodial wallet, sweeping HyperCore back to the EVM side, flushing the adapter, and moving
 * to the exit wallet. Each is its own HyperEVM transaction, so a withdrawal can sit on that one
 * phrase across several steps while the bar restarts each time — which reads as a stuck loop rather
 * than progress.
 *
 * Naming them individually, and saying which one of how many this is, turns the same wait into
 * something legible. Keyed on `step` (the state machine) rather than the backend's `message`
 * (copy), so wording can change here without a backend deploy.
 */
/** The operations that render a phase bar: the two custodial transfers and the V1 to V2 migration. */
export type OperationDirection = 'deposit' | 'withdraw' | 'migrate';

export interface PhaseLabel {
    label: string;
    /** 1-based position in this operation's sequence, when it has a known one. */
    index?: number;
    total?: number;
}

/**
 * The visible stages of each operation, in order. Each POSITION groups the backend steps that make
 * up one stage, so the count a user sees stays stable even though the state machine is finer
 * grained — a deposit that has to provision a wallet still reads "Step 1 of 5", not a sequence
 * that silently grows for some users and not others.
 */
const WITHDRAW_SEQUENCE: readonly (readonly string[])[] = [
    ['withdraw.preparing'],
    ['withdraw.hypercoreSweep.waiting'],
    ['withdraw.adapterFlush.waiting'],
    ['withdraw.exitSweep.waiting'],
    ['withdraw.relay.filling', 'withdraw.relay.bridging', 'bridging'],
];

const DEPOSIT_SEQUENCE: readonly (readonly string[])[] = [
    // backend preparation, plus the one-time wallet provisioning a first deposit performs
    ['validating', 'checkingTradingWallet', 'checkingOriginWallet', 'quotingRelay',
        'recordingTransfer', 'creatingWallet', 'walletReady'],
    ['awaitingWalletSignature'],
    ['submittingRelay'],
    ['bridging', 'deposit.relay.bridging'],
    ['deposit.hypercoreFunding.submitting', 'deposit.hypercoreFunding.waiting',
        'deposit.hypercoreFunding.deferred'],
];

const MIGRATE_SEQUENCE: readonly (readonly string[])[] = [
    ['migrate.preparing'],
    ['migrate.hypercoreSweep.waiting'],
    ['migrate.adapterFlush.waiting'],
    ['migrate.transfer.waiting'],
    ['migrate.gas.waiting'],
];

/** Which stage list each direction counts against. */
const SEQUENCES: Record<OperationDirection, readonly (readonly string[])[]> = {
    deposit: DEPOSIT_SEQUENCE,
    withdraw: WITHDRAW_SEQUENCE,
    migrate: MIGRATE_SEQUENCE,
};

const STEP_LABELS: Record<string, string> = {
    // withdraw — each of these was "Preparing withdrawal"
    'withdraw.preparing': 'Moving funds out of your wallet',
    'withdraw.hypercoreSweep.waiting': 'Bringing funds back from HyperCore',
    'withdraw.adapterFlush.waiting': 'Clearing your trading account',
    'withdraw.exitSweep.waiting': 'Moving funds to the bridge wallet',
    'withdraw.relay.filling': 'Bridging to your wallet',
    'withdraw.relay.bridging': 'Bridging to your wallet',

    // deposit — the preparation steps were previously unlabelled and fell back to the backend's
    // own copy, so the panel said "Preparing bridge route" with no sense of where that sat.
    validating: 'Checking your deposit',
    checkingTradingWallet: 'Checking your trading wallet',
    checkingOriginWallet: 'Checking your wallet balance',
    quotingRelay: 'Finding the best route',
    recordingTransfer: 'Recording your deposit',
    creatingWallet: 'Setting up your wallet',
    walletReady: 'Wallet ready',
    awaitingWalletSignature: 'Waiting for your signature',
    submittingRelay: 'Confirming your transaction',
    'deposit.relay.bridging': 'Bridging funds',
    // two transactions under one stage; naming each half is what tells the user the second wait
    // is progress, not the first one again
    'deposit.hypercoreFunding.submitting': 'Approving the move to your trading account',
    'deposit.hypercoreFunding.waiting': 'Crediting your trading account',
    'deposit.hypercoreFunding.deferred': 'Finishing up',

    bridging: 'Bridging funds',

    // migrate: V1 balance -> the Snappy Wallet, never leaving HyperEVM after the sweep
    'migrate.preparing': 'Preparing the migration',
    'migrate.hypercoreSweep.waiting': 'Bringing funds back from HyperCore',
    'migrate.adapterFlush.waiting': 'Clearing your trading account',
    'migrate.transfer.waiting': 'Sending to your Snappy Wallet',
    'migrate.gas.waiting': 'Adding a little HYPE for gas',
};

/**
 * Human label for a step, with its position when the operation has a known sequence.
 * Returns null when the step is unknown — the caller keeps the backend's own message.
 */
export function phaseLabel(
    step: string | null | undefined,
    direction?: OperationDirection,
): PhaseLabel | null {
    if (!step) return null;
    const label = STEP_LABELS[step];
    if (!label) return null;
    // `bridging` belongs to BOTH sequences at different positions, so the direction decides which
    // one to count against. Without it the deposit would be numbered against the withdraw stages.
    const sequence = SEQUENCES[direction ?? 'withdraw'];
    const position = sequence.findIndex(stage => stage.includes(step));
    if (position >= 0) {
        return { label, index: position + 1, total: sequence.length };
    }
    return { label };
}
